import * as React from 'react';
import { Helmet } from 'react-helmet-async';
import { Switch } from '../../../components/ui/switch';
import LabelWithInfo from '../../../components/LabelWithInfo/LabelWithInfo';
import CustomModal from '../../../components/CustomModal/CustomModal';

import ChatbotContext from '../../../context/ChatbotContext/ChatbotContext';
import AdvancedSettingsResponse from '../../../types/AdvancedSettingsResponse';
import SettingsService from '../../../services/SettingsService/SettingsService';
import LoadingSpinner from '../../../components/LoadingSpinner/LoadingSpinner';
import CustomInput from '../../../components/CustomInput/CustomInput';
import NotificationSnackbar from '../../../components/NotificationSnackbar/NotificationSnackbar';
import Button from '../../../components/Button/Button';

const DATA_RETENTION_CONFIRMATION = 'UPDATE DATA RETENTION';

function DataRetentionTab(): React.ReactElement {
  const { currentChatbot } = React.useContext(ChatbotContext);

  const [settings, setSettings] =
    React.useState<AdvancedSettingsResponse | null>(null);
  const [dataRetentionDays, setDataRetentionDays] = React.useState(0);
  const [piiEnabled, setPiiEnabled] = React.useState(false);

  const [isLoadingData, setIsLoadingData] = React.useState(true);
  const [isLoading, setIsLoading] = React.useState(false);
  const [dataErrorMessage, setDataErrorMessage] = React.useState('');
  const [errorMessage, setErrorMessage] = React.useState('');
  const [successMessage, setSuccessMessage] = React.useState('');
  const [showError, setShowError] = React.useState(false);
  const [showSuccess, setShowSuccess] = React.useState(false);

  const [isDataRetentionDialogOpen, setIsDataRetentionDialogOpen] =
    React.useState(false);
  const [dataRetentionInput, setDataRetentionInput] = React.useState('');
  const [dataRetentionConfirmation, setDataRetentionConfirmation] =
    React.useState('');

  React.useEffect(() => {
    const controller = new AbortController();

    if (currentChatbot) {
      const fetchSettings = async (): Promise<void> => {
        try {
          const response = await SettingsService.getAdvancedSettings(
            currentChatbot.id,
            controller.signal
          );
          setSettings(response);
          setDataRetentionDays(response.dataRetentionDays);
          setDataRetentionInput(response.dataRetentionDays.toString());
          setPiiEnabled(response.piiEnabled);
        } catch (err: any) {
          if (err.name !== 'CanceledError') {
            setDataErrorMessage('Failed to load settings');
          }
        } finally {
          setIsLoadingData(false);
        }
      };

      fetchSettings();
    } else {
      setIsLoadingData(false);
    }

    return (): void => controller.abort();
  }, [currentChatbot]);

  const persist = async (
    newRetentionDays: number,
    newPiiEnabled: boolean
  ): Promise<void> => {
    if (!currentChatbot || !settings) return;

    await SettingsService.updateAdvancedSettings(currentChatbot.id, {
      dataRetentionDays: newRetentionDays,
      piiEnabled: newPiiEnabled,
      chatbotPurpose: settings.chatbotPurpose,
      styleHints: settings.styleHints,
      aiModel: settings.aiModel,
      aiTemperature: settings.aiTemperature,
      summarisationAiModel: settings.summarisationAiModel,
      healthScoreAiModel: settings.healthScoreAiModel
    });
  };

  const handleDataRetentionDialogSave = async (): Promise<void> => {
    const newDays = Number.parseInt(dataRetentionInput, 10);
    if (Number.isNaN(newDays) || newDays < 1) {
      setErrorMessage('Data retention days must be a positive number');
      setShowError(true);
      return;
    }
    if (dataRetentionConfirmation !== DATA_RETENTION_CONFIRMATION) {
      setErrorMessage(
        `Please type "${DATA_RETENTION_CONFIRMATION}" to confirm`
      );
      setShowError(true);
      return;
    }

    try {
      setIsLoading(true);
      await persist(newDays, piiEnabled);
      setDataRetentionDays(newDays);
      setSuccessMessage('Data retention settings updated');
      setShowSuccess(true);
      setIsDataRetentionDialogOpen(false);
      setDataRetentionConfirmation('');
    } catch (err: any) {
      if (err.name !== 'CanceledError') {
        setErrorMessage(err.message || 'Something went wrong');
        setShowError(true);
      }
    } finally {
      setIsLoading(false);
    }
  };

  const handlePiiChange = async (checked: boolean): Promise<void> => {
    setPiiEnabled(checked);
    setShowError(false);
    setShowSuccess(false);

    try {
      setIsLoading(true);
      await persist(dataRetentionDays, checked);
      setSuccessMessage('Settings updated');
      setShowSuccess(true);
    } catch (err: any) {
      setPiiEnabled(!checked);
      if (err.name !== 'CanceledError') {
        setErrorMessage(err.message || 'Something went wrong');
        setShowError(true);
      }
    } finally {
      setIsLoading(false);
    }
  };

  if (isLoadingData) return <LoadingSpinner size={200} />;
  if (dataErrorMessage)
    return (
      <NotificationSnackbar
        message={dataErrorMessage}
        severity='error'
        open
        onClose={() => setDataErrorMessage('')}
      />
    );

  return (
    <div className='flex flex-1 flex-col'>
      <Helmet>
        <title>Japeto - Data Retention</title>
      </Helmet>

      <div className='w-full max-w-md space-y-4'>
        <div className='space-y-2'>
          <LabelWithInfo
            htmlFor='data-retention-days'
            info='How long conversation data is kept before it is automatically deleted.'
          >
            Data retention period
          </LabelWithInfo>
          <div className='flex items-start justify-between gap-4'>
            <div className='text-sm text-muted-foreground flex-1'>
              <p className='mb-1'>
                <strong>{dataRetentionDays}</strong> days
              </p>
              <p className='text-xs text-muted-foreground'>
                Data is automatically deleted after this many days. Lowering
                this value will trigger immediate deletion of data older than
                the new threshold.
              </p>
            </div>
            <Button
              type='button'
              className='whitespace-nowrap'
              onClick={() => setIsDataRetentionDialogOpen(true)}
            >
              Update
            </Button>
          </div>
        </div>

        <CustomModal
          open={isDataRetentionDialogOpen}
          onClose={() => setIsDataRetentionDialogOpen(false)}
          title='Update Data Retention Period'
          maxWidth='md'
        >
          <div className='flex flex-col gap-4'>
            <div className='bg-red-50 border border-red-200 rounded p-3 mb-2'>
              <p className='text-sm text-red-800'>
                <strong>Warning:</strong> Lowering data retention will trigger
                immediate deletion of data older than the new threshold. This
                action cannot be undone.
              </p>
            </div>
            <CustomInput
              label='Days'
              type='number'
              value={dataRetentionInput}
              onChange={(e) => setDataRetentionInput(e.target.value)}
              name='dataRetentionDays'
            />
            <CustomInput
              label={`Confirm by typing ${DATA_RETENTION_CONFIRMATION}`}
              type='text'
              name='dataRetentionConfirmation'
              placeholder={`Type ${DATA_RETENTION_CONFIRMATION} to confirm`}
              value={dataRetentionConfirmation}
              onChange={(e) => setDataRetentionConfirmation(e.target.value)}
            />
            <div className='flex justify-end gap-3 mt-6'>
              <Button
                variant='outline'
                onClick={() => setIsDataRetentionDialogOpen(false)}
              >
                Cancel
              </Button>
              <Button
                onClick={handleDataRetentionDialogSave}
                loading={isLoading}
                disabled={
                  dataRetentionConfirmation !== DATA_RETENTION_CONFIRMATION
                }
              >
                Save changes
              </Button>
            </div>
          </div>
        </CustomModal>

        <div className='flex items-center justify-between mt-4'>
          <LabelWithInfo
            className='text-sm font-medium'
            info='Automatically detect and mask personal information (names, emails, phone numbers) in conversations.'
          >
            Enable PII redaction
          </LabelWithInfo>
          <div className='flex items-center space-x-2'>
            <span className='text-sm'>Off</span>
            <Switch
              checked={piiEnabled}
              disabled={isLoading}
              onCheckedChange={handlePiiChange}
            />
            <span className='text-sm'>On</span>
          </div>
        </div>
      </div>

      <NotificationSnackbar
        message={successMessage}
        severity='success'
        open={showSuccess}
        onClose={() => setShowSuccess(false)}
      />
      <NotificationSnackbar
        message={errorMessage}
        severity='error'
        open={showError}
        onClose={() => setShowError(false)}
      />
    </div>
  );
}

export default DataRetentionTab;
